import { UIIcon } from '../../config/icons';
import { $symbols } from '../../config/symbols';
import { sUploadImage } from '../mdl/s-upload';
import { uiSetBG } from '../ui/bg';
import { UIButton } from '../ui/component/button';
import { UIColorInput } from '../ui/component/color';
import { UIDropdown, valueDropdownGenerator } from '../ui/component/dropdown';
import { UIImageInput } from '../ui/component/image';
import { UINumberInput } from '../ui/component/input';
import { UILabeled } from '../ui/component/labeled';
import { UIList } from '../ui/component/list';
import { UISelect } from '../ui/component/select';
import { UICheckbox } from '../ui/component/toggle';
import { UIContent, UIDropDelegate, isImageMime, uiInitDropTarget } from '../ui/dnd';
import { $el } from '../ui/elements';
import { uiMakeValuesFromObject, uiSetPrefixedClass } from '../ui/functions';
import { uiStartOrder } from '../ui/order';
import { onInteract } from '../ui/touch';
import { UIBackground, UIClassesSpec, UIValue } from '../ui/types';
import { UIView, UIViewSpec } from '../ui/view';
import { $log } from '../y/debug';
import { $on, $trigger, YChangeEvent, YEventHandlers, YInputEvent, YStateEvent, YValueEvent } from '../y/events';
import { YInput } from '../y/form';
import { YLanguage } from '../y/i18n';
import { yCopy } from '../y/object';
import { YId } from '../y/types';
import { EDBlock } from './block';
import { EDObject } from './editor';

interface EDImage {
    image: YId;
    width: number;
    height: number;
    aspect: number;
}

type EDI18NImage = ({ [lang in YLanguage]?: EDImage; } & { all?: undefined }) | { all: EDImage };
type EDBackgroundKind = 'none' | 'color' | 'gradient' | 'image' | 'video';
type EDBackgroundGradient = 'linear' | 'radial';

export interface EDBackgroundPreset {
    name: string;
    bg: UIBackground;
}

const background_kind_values: UIValue<EDBackgroundKind>[] = [{ key: 'none', name: 'None' }, { key: 'color', name: 'Color' }, { key: 'gradient', name: 'Gradient' }, { key: 'image', name: 'Image' }, { key: 'video', name: 'Video' }];
const background_gradient_values: UIValue<EDBackgroundGradient>[] = [{ key: 'linear', name: 'Linear' }, { key: 'radial', name: 'Radial' }];

export class EDBackgroundInput extends UIView implements YInput<UIBackground | undefined> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<UIBackground | undefined> & YChangeEvent>;

    private _kind: UISelect<EDBackgroundKind>;
    private _color: UIColorInput;
    private _gradient: UISelect<EDBackgroundGradient>;
    private _from: UIColorInput;
    private _to: UIColorInput;
    private _image: UIImageInput;

    okay = true;
    dirty = false;
    disabled = false;

    constructor(presets: Record<string, EDBackgroundPreset>, scope: string) {
        super('div', 'l-form');

        const kind = this._kind = new UISelect(background_kind_values, { kind: 'k-subtle' });
        const color = this._color = new UIColorInput({ kind: 'k-subtle' });
        const gradient = this._gradient = new UISelect(background_gradient_values, { kind: 'k-subtle' });
        const from = this._from = new UIColorInput({ kind: 'k-subtle' });
        const to = this._to = new UIColorInput({ kind: 'k-subtle' });
        const image = this._image = new UIImageInput({ scope });

        $on(kind, 'value', v => {
            color.ui_show(undefined, v == 'color');
            gradient.ui_show(undefined, v == 'gradient');
            from.ui_show(undefined, v == 'gradient');
            to.ui_show(undefined, v == 'gradient');
            image.ui_show(undefined, v == 'image');

            $trigger(this, 'change');
        });

        $on(color, 'value', () => {
            if(kind.value == 'color')
                $trigger(this, 'change');
        });

        $on(gradient, 'value', () => {
            if(kind.value == 'gradient')
                $trigger(this, 'change');
        });

        $on(from, 'value', () => {
            if(kind.value == 'gradient')
                $trigger(this, 'change');
        });

        $on(to, 'value', () => {
            if(kind.value == 'gradient')
                $trigger(this, 'change');
        });

        $on(image, 'value', () => {
            if(kind.value == 'image')
                $trigger(this, 'change');
        });

        const preset = new UIButton({
            icon: UIIcon.NOTEBOOK, on_click: () => {
                const dropdown = new UIDropdown(uiMakeValuesFromObject(presets), valueDropdownGenerator);
                dropdown.on_choose = (v) => {
                    this.value = presets[v.key].bg;
                };
                dropdown.open(preset);
            }
        })

        this.ui_add([
            $el.DIV('l-input-row', [kind, preset]),
            new UILabeled(color, { above: 'Color' }),
            new UILabeled(gradient, { above: 'Gradient' }),
            new UILabeled(from, { above: 'From' }),
            new UILabeled(to, { above: 'To' }),
            new UILabeled(image, { above: 'Image' }),
        ]);
    }

    get value() {
        const kind = this._kind.value;

        if(kind == 'color') {
            return { color: this._color.value || '#FFFFFF' };
        } else if(kind == 'gradient') {
            return {
                gradient: this._gradient.value as 'linear',
                stops: [
                    [this._from.value || '#FFFFFF'],
                    [this._to.value || '#FFFFFF']
                ]
            };
        } else if(kind == 'image') {
            return { media: this._image.value };
        } else if(kind == 'video') {
            return { special: 'video' };
        }

        return {}
    }

    set value(v: UIBackground | undefined) {
        v = v || {};

        if(v.color) {
            this._kind.value = 'color';
            this._color.value = v.color;
            return;
        } else if(v.gradient) {
            this._kind.value = 'gradient';
            this._gradient.value = v.gradient;
            this._from.value = v.stops[0][0];
            this._to.value = v.stops[1][0];
        } else if(v.media) {
            this._kind.value = 'image';
            this._image.value = v.media;
        } else if(v.special == 'video') {
            this._kind.value = 'video';
        } else {
            this._kind.value = 'none';
        }
    }
}

export function emptyImage(): EDImage {
    return { width: 0, height: 0, aspect: 1 } as EDImage;
}

interface EDImageInputOptions {
    scope: string;
    fixed_width?: boolean;
}

export class EDImageInput extends UIView implements YInput<EDImage>, UIDropDelegate {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<EDImage> & YChangeEvent>;

    private _value: EDImage;
    private _holder: HTMLElement;
    private _width: UINumberInput;
    private _scope: string;
    okay = true;
    dirty = false;
    disabled = false;

    constructor(options: EDImageInputOptions) {
        super('div', 'l-form');

        this._value = emptyImage();
        this._scope = options.scope;

        const holder = this._holder = $el.DIV('ui-image-input');
        const holder_labeled = new UILabeled(holder, { above: 'Image' });
        const width = this._width = new UINumberInput();

        this.ui_add([
            holder_labeled,
            !options.fixed_width && new UILabeled(width, { above: 'Width' }),
        ]);

        uiInitDropTarget(holder, this);

        if(!options.fixed_width) {
            $on(width, 'value', v => {
                if(v == undefined || v == this._value.width) return;
                this._value.width = v;
                $trigger(this, 'change');
            });
        }
    }

    dropEnter(content: UIContent) {
        return content.type == 'files';
    }

    drop(content: UIContent) {
        if(content.type == 'files') {
            const valid_files = (content.value as File[]).filter((f: File) => isImageMime(f.type));
            if(valid_files.length == 1) {
                sUploadImage(valid_files[0], this._scope).then(i => {
                    this._value.height = Math.round(i.info.height / 2);
                    this._value.width = Math.round(i.info.width / 2);
                    this._value.aspect = i.info.width / i.info.height;
                    this._value.image = i.id;
                    uiSetBG(this._holder, { media: i.id, size: 'contain' });
                    this._width.value = this._value.width;
                    $trigger(this, 'change');
                }, err => {
                    $log('editor', 'failed', err);
                });
            }
        }
    }

    get value(): EDImage {
        return this._value;
    }

    set value(v: EDImage | undefined) {
        v = v ? yCopy(v) : emptyImage();
        this._value = v;
        uiSetBG(this._holder, { media: v.image, size: 'contain' });
        this._width.value = v.width;
        $trigger(this, 'change');
    }
}

export class EDImageObject extends UIView implements EDObject, YInput<EDI18NImage>, UIDropDelegate {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<EDI18NImage> & YChangeEvent>;

    properties: UIViewSpec;
    parent: EDBlock<any>;
    private _multilanguage: UICheckbox;
    private _language: YLanguage;
    private _value: EDI18NImage;
    private _width: UINumberInput;
    private _height: UINumberInput;
    private _current: EDImage;
    private _aspect: boolean | undefined;
    private _scope: string;
    private _remove_image?: (image: EDImageObject) => void;
    okay = true;
    dirty = false;
    disabled = false;
    name = 'Image';

    constructor(block: EDBlock<any>, scope: string, list?: UIList, aspect?: boolean, classes?: UIClassesSpec, remove_image?: (image: EDImageObject) => void) {
        super('div', ['ed-image ed-object', aspect && 'm-aspect', classes]);
        this.ui_elem.ed_object = this;
        this.parent = block;

        this._language = 'en';
        this._scope = scope;
        this._value = { all: this._current = emptyImage() };
        this._aspect = aspect;
        this._remove_image = remove_image;

        const width = this._width = new UINumberInput();
        const height = this._height = new UINumberInput();
        const multilanguage = this._multilanguage = new UICheckbox({ title: 'Multilanguage' });

        this.properties = $el.DIV('l-form', [
            multilanguage,
            new UILabeled(width, { above: 'Width' }),
            new UILabeled(height, { above: 'Height' })
        ]);

        if(aspect)
            height.ui_hide(); // TODO: show it but disabled

        if(list && !block.editor.translate_mode) {
            onInteract(this, e => uiStartOrder(e, list.onOrder(this)));
            this.root.addEventListener('click', e => {
                block.editor.selectObject(this);
                e.stopPropagation();
            });
        }

        if(!block.editor.translate_mode)
            uiInitDropTarget(this);

        this.root.ui_watch('ui-edit-language', l => {
            this._language = l;
            const v = this._value;
            if(!v.all)
                this._setCurrent(v[l] ??= yCopy(this._current));
        });

        this.root.ui_listen('ui-copy-language', l => {
            const v = this._value;
            if(!v.all) {
                v[l[1]] = yCopy(v[l[0]]);
                if(l[1] == this._language)
                    this._setCurrent(v[l[1]]!);
            }
        });

        this.root.ui_listen('ui-translate-language', l => {
            const v = this._value;
            if(!v.all) {
                v[l[1]] = yCopy(v[l[0]]);
                if(l[1] == this._language)
                    this._setCurrent(v[l[1]]!);
            }
        });

        $on(width, 'value', v => {
            if(v == undefined || v == this._current.width) return;
            this._current.width = v;
            this.ui_elem.style.width = v + 'px';
        });

        $on(height, 'value', v => {
            if(v == undefined || v == this._current.height) return;
            this._current.height = v;
            this.ui_elem.style.height = v + 'px';
        });

        $on(multilanguage, 'value', v => {
            if(v == !this._value.all) return;
            this._value = v ? { [this._language]: this._current } : { all: this._current };
        });
    }

    doRemove(): void {
        this._remove_image?.(this);
    }

    dropEnter(content: UIContent) {
        return content.type == 'files';
    }

    drop(content: UIContent) {
        if(content.type == 'files') {
            const valid_files = (content.value as File[]).filter((f: File) => isImageMime(f.type));
            if(valid_files.length == 1) {
                sUploadImage(valid_files[0], this._scope).then(i => {
                    this._current.height = Math.round(i.info.height / 2);
                    this._current.width = Math.round(i.info.width / 2);
                    this._current.aspect = i.info.width / i.info.height;
                    this._current.image = i.id;
                    this._setCurrent(this._current);
                }, err => {
                    $log('editor', 'failed', err);
                });
            }
        }
    }

    private _setCurrent(v: EDImage) {
        if(!v.aspect && v.height)
            v.aspect = v.width / v.height;
        this._current = v;
        uiSetBG(this, { media: v.image, size: '100% 100%' });
        const elem = this.root;
        elem.style.setProperty('--ui-width', `${v.width}px`);
        if(this._aspect)
            elem.style.setProperty('--ui-aspect', `${v.aspect}`);
        else
            elem.style.setProperty('--ui-height', `${v.height}px`);
        this._width.value = v.width;
        this._height.value = v.height;
        this.classList.toggle('f-empty', !v.image);
    }

    get value(): EDI18NImage {
        return this._value;
    }

    set value(v: EDI18NImage | undefined) {
        v = v ? yCopy(v) : { all: emptyImage() };
        this._value = v;
        this._multilanguage.value = !v.all;
        this._setCurrent(v.all || (v[this._language] ??= emptyImage()));
    }
}

const symbols_values: UIValue<string | undefined>[] = $symbols.all.map(s => ({ key: s, name: s }));
symbols_values.unshift({ key: undefined, name: 'None' });

export class EDSymbolInput extends UISelect<string | undefined> {
    constructor(target: UIViewSpec) {
        super(symbols_values, { kind: 'k-subtle' });
        target.ui_hide();
        $on(this, 'value', v => {
            if(!v) {
                target.ui_hide();
            } else {
                target.ui_show();
                uiSetPrefixedClass(target, 'sym', v);
            }
        });
    }
}
